import dayjs from 'dayjs';

import {
  parseAppVersion,
  splitAppVersionString,
} from '@/common/AppVersionInput';
import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';
import { FACETS } from '@/constants/facets';
import Service, { StageTypes } from '@/lib/App';
import { Constraint, RolloutTarget } from '@/lib/PlannerDTOs';

type getTitleFunction = (value: string) => string;

export function getRolloutTargetIndex(
  rolloutTarget: RolloutTarget,
  rolloutTargets: RolloutTarget[],
): number {
  if (!rolloutTargets?.length) return -1;

  const index = rolloutTargets.findIndex(
    (target) => target?.id === rolloutTarget?.id,
  );

  return index > -1 ? index + 1 : index;
}

export function getRolloutTargetName(constraints: Constraint[]): string {
  // Get Facet Constriants Map
  const facetConstraintMap: { [key: string]: Constraint[] } = {};
  if (constraints) {
    constraints.forEach((constraint: Constraint) => {
      if (!constraint?.target) return;

      // Ensure constraints are processed regardless of type field
      // Legacy constraints may have type: "" instead of ConstraintType.Standard
      // Both should be treated equally for rollout target name generation
      if (!facetConstraintMap?.[constraint.target]) {
        facetConstraintMap[constraint.target] = [];
      }

      facetConstraintMap[constraint.target!].push(constraint);
    });
  }

  // Get Title For Each Constraint
  const facetTitles: { [facet: string]: string } = {};
  const unsupportedFacetTitles: { [facet: string]: string } = {};

  Object.keys(facetConstraintMap).forEach((facet) => {
    const facetConstraints: Constraint[] = facetConstraintMap[facet];

    const titles: string[] = getTitlesForFacet(facet, facetConstraints);

    if (Service.cache.getFacet(facet)) facetTitles[facet] = titles.join(', ');
    else unsupportedFacetTitles[facet] = titles.join(', ');
  });

  // Sort the facets based on their priority
  const sortedFacetTitles: string[] = [];

  Service.cache.getAllFacets().forEach((facet) => {
    if (facetTitles[facet.name] != null) {
      sortedFacetTitles.push(facetTitles[facet.name]);
    }
  });

  Object.values(unsupportedFacetTitles).forEach((titles: string) => {
    sortedFacetTitles.push(titles);
  });

  if (sortedFacetTitles.length === 0) {
    return 'Global Rollout';
  }

  return sortedFacetTitles.join(' & ');
}

function getTitle(
  opConstriants: {
    [key: string]: Constraint[];
  },
  titleFn: getTitleFunction,
): string[] {
  const titles: string[] = [];
  Object.keys(opConstriants).forEach((op) => {
    const constraints: Constraint[] = opConstriants[op];
    constraints.forEach((constraint) => {
      const facetDisplayName =
        Service.cache.getFacet(constraint?.target)?.title || constraint?.target;

      switch (constraint.op) {
        case '/':
          titles.push(
            `${(+constraint.value! * 100).toFixed(0)}% ${facetDisplayName}`,
          );
          break;
        default:
          titles.push(
            `${facetDisplayName} ${op} ${titleFn(constraint.value!)}`,
          );
          break;
      }
    });
  });
  return titles;
}

export function parseCountryInlineGroup(value: string): string[] {
  if (!value || value === '[]') {
    return [];
  }

  return value.startsWith('[') && value.endsWith(']')
    ? value
        .slice(1, -1)
        .split(',')
        .map((s) => s.trim())
    : [value.toString()];
}

const getVehicleNameFromID = (id: string): string => {
  const vehicle = Service.cache!.getVehicle(parseInt(id, 10));
  if (!vehicle) {
    return id + '(⚠️ Disabled)';
  }
  const city = Service.cache!.getCity(vehicle.cityID);
  return city!.name + ': ' + vehicle.name;
};

const getDefaultTitle: getTitleFunction = (value: string): string => {
  return value;
};

const getCountryTitle: getTitleFunction = (value: string): string => {
  // country value will be inline group
  const countries = parseCountryInlineGroup(value).map((countryCode) => {
    return getCountryNameByCountryCode(countryCode);
  });

  if (countries.length <= 3) {
    return countries.join(', ');
  }

  return (
    countries.slice(0, 3).join(', ') +
    ' and ' +
    (countries.length - 3) +
    ' more countries'
  );
};

const getCityTitle: getTitleFunction = (value: string): string => {
  const cityIds: string[] = value.startsWith('[')
    ? (JSON.parse(value) as string[])
    : [value.toString()];

  const cities = cityIds.map((id: string): string => {
    if (id === null || id === '') {
      return '?';
    }

    const city = Service.cache!.getCity(parseInt(id, 10));
    if (city != null) {
      return city.name;
    }

    return '?';
  });

  if (cities.length <= 3) {
    return cities.join(', ');
  }

  return (
    cities.slice(0, 3).join(', ') +
    ' and ' +
    (cities.length - 3) +
    ' more cities'
  );
};

const getVehicleTypeTitle: getTitleFunction = (value: string): string => {
  const vehicleIds: string[] = value.startsWith('[')
    ? (JSON.parse(value) as string[])
    : [value.toString()];

  const vehicles = vehicleIds.map((id: string): string => {
    if (id == null || id === '') {
      return '?';
    }
    const vehicleName = getVehicleNameFromID(id);
    return vehicleName || '?';
  });

  if (vehicles.length <= 3) {
    return vehicles.join(', ');
  }

  vehicles.sort(function (vehicle1, vehicle2) {
    if (vehicle1.includes('⚠️') && vehicle2.includes('⚠️')) {
      return 0;
    }
    if (vehicle1.includes('⚠️')) {
      return -1;
    }
    if (vehicle2.includes('⚠️')) {
      return 1;
    }
    return 0;
  });

  return (
    vehicles.slice(0, 3).join(', ') +
    ' and ' +
    (vehicles.length - 3) +
    ' more vehicles'
  );
};

const getAppVersionTitle: getTitleFunction = (value: string): string => {
  const appVersions = splitAppVersionString(value);
  const texts = appVersions.map(function (appVersion) {
    const [app, os, version] = parseAppVersion(appVersion);
    return `v${version} (${app} ${os})`;
  });
  return texts.join(' OR ');
};

const commaSepratedStringArrayRegex = /^\[(([a-z,]+[,\s]?)+)\]$/;

const getDevelopmentStageTitle: getTitleFunction = (value: string): string => {
  const match = value.match(commaSepratedStringArrayRegex);
  if (match) {
    const stages = match[1].split(',');
    return stages.map((stage) => `'${StageTypes[stage.trim()]}'`).join(', ');
  }
  return value;
};

function getTitlesForFacet(facet: string, constraints: Constraint[]): string[] {
  const opConstriants: { [key: string]: Constraint[] } = {};
  constraints.forEach((constraint) => {
    if (opConstriants[constraint.op!] == null) {
      opConstriants[constraint.op!] = [];
    }

    opConstriants[constraint.op!].push(constraint);
  });

  switch (facet) {
    case FACETS.Country: {
      return getTitle(opConstriants, getCountryTitle);
    }
    case FACETS.City: {
      return getTitle(opConstriants, getCityTitle);
    }
    case FACETS.VehicleTypeID: {
      return getTitle(opConstriants, getVehicleTypeTitle);
    }
    case FACETS.Device: {
      return getTitle(opConstriants, getAppVersionTitle);
    }
    case FACETS.DevelopmentStage: {
      return getTitle(opConstriants, getDevelopmentStageTitle);
    }
    default: {
      return getTitle(opConstriants, getDefaultTitle);
    }
  }
}

export function getVariableStatsStartDate(): string {
  const startOfTheDay = dayjs().startOf('day');
  const dayBefore = startOfTheDay.subtract(7, 'day');
  const dayBeforeInUTCFormat = dayBefore.utc().format('YYYY-MM-DDTHH:mm:ss');

  return `${dayBeforeInUTCFormat}Z`;
}
